import { FC, Suspense, useCallback, useContext } from 'react';
import styled from '@emotion/styled';
import { ErrorBoundary } from '@sentry/nextjs';
import { useSetAtom } from 'jotai';
import { HEADER_HEIGHT, TABLET_HEADER_HEIGHT } from 'src/components/Header/styles';
import ItemInventoryModal from 'src/components/ItemInventory/Modal';
import { ModalKeyContext } from 'src/components/Modal';
import ModalLayoutBase, { ModalLayoutProps } from 'src/components/ModalLayout';
import ShopLoading from 'src/components/Shop/Loading';
import useCssMediaDevice from 'src/hooks/useCssMediaDevice';
import { closeModalAtom, showModalAtom } from 'src/stores/modal/atoms';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import ProductLoadingFailure from './ProductLoadingFailure';
import Shop from './Shop';
import ShopHeader from './ShopHeader';
const ModalLayout = styled(ModalLayoutBase)`
  position: fixed;
  inset-inline-end: 24px;
  top: ${HEADER_HEIGHT};
  max-height: calc(var(--100vh) - ${HEADER_HEIGHT} - 24px);
  width: 430px;
  min-width: 430px;
  max-width: 430px;
  overflow: hidden;

  ${({
  theme
}) => theme.screenSize.tablet} {
    max-height: calc(var(--100vh) - ${TABLET_HEADER_HEIGHT} - 24px);
    inset-inline-end: 16px;
    top: ${TABLET_HEADER_HEIGHT};
  }

  ${({
  theme
}) => theme.screenSize.mobile} {
    --background-color: ${({
  theme
}) => theme.color.bg1};
    max-height: var(--100vh);
    width: 100%;
    top: 0;
    inset-inline-start: 0;
    inset-inline-end: 0;
  }
`;
const ShopModal: FC<ModalLayoutProps> = props => {
  const {
    onClose,
    ...rest
  } = props;
  const modalKey = useContext(ModalKeyContext);
  const device = useCssMediaDevice();
  const showModal = useSetAtom(showModalAtom);
  const closeModal = useSetAtom(closeModalAtom);
  const handleDirectClose = useCallback(() => {
    onClose?.();
    if (modalKey) closeModal(modalKey);
  }, [onClose, modalKey, closeModal]);
  const handleInventory = useCallback(() => {
    if (modalKey) closeModal(modalKey);
    showModal({
      key: ModalType.ITEM_INVENTORY,
      component: () => <ItemInventoryModal lockBodyScroll={device !== 'desktop'} />
    });
  }, [closeModal, device, modalKey, showModal]);
  return <ModalLayout {...rest} lockBodyScroll={device === 'mobile'} onClose={onClose} mobileNavButtonType={ModalNavButtonType.NONE} data-testid='Shop-Modal' data-sentry-element="ModalLayout" data-sentry-component="ShopModal" data-sentry-source-file="ShopModal.tsx">
      <ShopHeader onClose={handleDirectClose} onInventoryClick={handleInventory} data-sentry-element="ShopHeader" data-sentry-source-file="ShopModal.tsx" />
      <ErrorBoundary fallback={({
      resetError
    }) => <ProductLoadingFailure resetError={resetError} />} data-sentry-element="ErrorBoundary" data-sentry-source-file="ShopModal.tsx">
        <Suspense fallback={<ShopLoading />} data-sentry-element="Suspense" data-sentry-source-file="ShopModal.tsx">
          <Shop data-sentry-element="Shop" data-sentry-source-file="ShopModal.tsx" />
        </Suspense>
      </ErrorBoundary>
    </ModalLayout>;
};
export default ShopModal;